import { Button, IconButton, Spinner } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { MdFileUpload, MdPrint } from 'react-icons/md';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { PdfGeneratorProps } from './PdfGenerator.props';

const PdfGenerator = ({
  fileName,
  documentTemplate: DocumentTemplate,
  data,
  type = 'icon',
}: PdfGeneratorProps) => {
  const [blobData, setBlobData] = useState<Blob | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [pdfReady, setPdfReady] = useState<boolean>(false);

  const handleDownload = () => {
    if (blobData) {
      // If PDF is already generated, download it
      const link = document.createElement('a');
      const blobUrl = window.URL.createObjectURL(blobData);
      link.href = blobUrl;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl); // Clean up the Blob URL after download
    }
  };

  useEffect(() => {
    // If loading is true and PDF is generated, auto download
    if (blobData && loading) {
      handleDownload();
      setLoading(false);
    }
  }, [blobData, loading]);

  return (
    <>
      {loading ? (
        <Spinner color="primary.500" size="sm" />
      ) : (
        <>
          {type === 'icon' && (
            <IconButton
              onClick={() => {
                if (pdfReady) {
                  handleDownload();
                } else {
                  setLoading(true);
                }
              }}
              size="xs"
              variant="ghost"
              color="support.500"
              _hover={{ color: 'primary.300' }}
              _active={{ color: 'primary.200' }}
              aria-label="print-report"
              icon={<MdPrint size="18px" />}
            />
          )}

          {type === 'button' && (
            <Button
              onClick={() => {
                if (pdfReady) {
                  handleDownload();
                } else {
                  setLoading(true);
                }
              }}
              leftIcon={<MdFileUpload />}>
              Export PDF
            </Button>
          )}
        </>
      )}

      {loading && !blobData && (
        <PDFDownloadLink
          document={<DocumentTemplate data={data} />}
          fileName={fileName}>
          {({ blob, loading }: { blob: Blob | null; loading: boolean }) => {
            if (!loading && blob && !blobData) {
              setBlobData(blob);
              setPdfReady(true);
            }

            return null; // Do not render any additional components
          }}
        </PDFDownloadLink>
      )}
    </>
  );
};

export default PdfGenerator;
